import { useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { tableStyles } from "../../../TableStyle";

const toNumber = (v) => (isFinite(Number(v)) ? Number(v) : 0);
const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MAX_NOTE_WORDS = 12;

const truncateNote = (note) => {
  const trimmed = String(note || "").trim();
  if (!trimmed) return "—";

  const words = trimmed.split(/\s+/);
  if (words.length <= MAX_NOTE_WORDS) return trimmed;

  return `${words.slice(0, MAX_NOTE_WORDS).join(" ")}...`;
};

const DetailRow = ({ label, value }) => (
  <div>
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography variant="body2">{value || "—"}</Typography>
  </div>
);

const CashDonationsTable = ({ t, data }) => {
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const columnsCash = useMemo(
    () => [
      { name: t("inserted_on"), selector: (row) => row.date, sortable: true },
      { name: t("inserted_by"), selector: (row) => row.inserted_by, sortable: true },
      { name: t("date_of_donations"), selector: (row) => row.date_donated, sortable: true },
      {
        name: t("cash_amount"),
        selector: (row) => toNumber(row.cashAmount),
        cell: (row) => `${formatCurrency(row.cashAmount)} $`,
        sortable: true,
        right: true,
      },
      {
        name: t("notes", { defaultValue: "Notes" }),
        selector: (row) => row.notes || "",
        cell: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
            title={row.notes || ""}
          >
            {truncateNote(row.notes)}
          </div>
        ),
        sortable: true,
        grow: 2,
      },
    ],
    [t]
  );

  return (
    <div className="flex flex-1 flex-col">
      <div style={{ fontSize: 20 }}>{t("cash_donations")}</div>
      <DataTable
        highlightOnHover
        customStyles={tableStyles}
        columns={columnsCash}
        data={data}
        pagination
        pointerOnHover
        onRowClicked={(row) => setSelectedTransaction(row)}
      />

      <Dialog
        open={Boolean(selectedTransaction)}
        onClose={() => setSelectedTransaction(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("cash_donation_details", { defaultValue: "Cash donation details" })}</DialogTitle>
        <DialogContent
          dividers
          sx={{
            paddingTop: "20px !important",
            paddingBottom: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <DetailRow label={t("inserted_on")} value={selectedTransaction?.date} />
          <DetailRow label={t("inserted_by")} value={selectedTransaction?.inserted_by} />
          <DetailRow label={t("date_of_donations")} value={selectedTransaction?.date_donated} />
          <DetailRow
            label={t("cash_amount")}
            value={selectedTransaction ? `${formatCurrency(selectedTransaction.cashAmount)} $` : "—"}
          />
          <DetailRow
            label={t("notes", { defaultValue: "Notes" })}
            value={selectedTransaction?.notes}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTransaction(null)} variant="contained">
            {t("close", { defaultValue: "Close" })}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CashDonationsTable;
